<template>
  <section class="space-y-5">
    <header>
      <h2 class="text-lg font-semibold text-gray-900">Support Our Campaign Modal</h2>
      <p class="text-sm text-gray-500">
        Right-column hero shown in the supporting-campaign wizard at each step. Each slot has an
        image and a short caption. Leave a slot blank to fall back to the global default.
      </p>
    </header>

    <!-- Step tabs drive the live-preview iframe so the admin can see
         each step's hero render without authenticating through the
         wizard. The clicked tab also expands the matching editor card
         below by anchor scroll. -->
    <div
      class="sticky top-0 z-10 -mx-1 flex gap-1 rounded-md border border-gray-200 bg-white p-1 shadow-sm"
      role="tablist"
      aria-label="Preview step"
    >
      <button
        v-for="(step, i) in steps"
        :key="step.key"
        type="button"
        role="tab"
        :aria-selected="(activeStep === i + 1).toString()"
        :class="[
          'flex-1 rounded px-2.5 py-1.5 text-xs font-semibold transition-colors',
          activeStep === i + 1
            ? 'bg-pink-500 text-white shadow-sm'
            : 'text-gray-600 hover:bg-gray-100',
        ]"
        @click="setActiveStep(i + 1)"
      >
        <span class="opacity-70 mr-1">{{ i + 1 }}</span>
        {{ step.label }}
      </button>
    </div>

    <div
      v-for="(step, i) in steps"
      :key="step.key"
      :ref="el => (cardRefs[i] = el)"
      :class="[
        'rounded-lg border bg-gray-50 p-4 space-y-3 transition-shadow',
        activeStep === i + 1
          ? 'border-pink-300 shadow-[0_0_0_3px_rgba(236,72,153,0.12)]'
          : 'border-gray-200',
      ]"
      @focusin="setActiveStep(i + 1, { skipScroll: true })"
    >
      <div class="flex items-center gap-2">
        <span
          class="inline-flex h-6 w-6 items-center justify-center rounded-full bg-pink-500 text-xs font-semibold text-white"
          aria-hidden="true"
        >{{ i + 1 }}</span>
        <span class="text-xs font-semibold uppercase tracking-wider text-gray-700">
          {{ step.label }}
        </span>
        <span class="text-xs italic text-gray-400">{{ step.role }}</span>
      </div>

      <TemplateImageInput
        v-model="content.steps[i].image"
        v-model:focal-point="content.steps[i].focal_point"
        :upload-url="uploadUrl"
        subfolder="support-modal"
        label="Image"
        help-text="Click the image to set the focal point — drives where the subject stays visible after the short-banner crop on mobile."
        :focal-default="defaultFocal"
      />

      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Caption</label>
        <input
          v-model="content.steps[i].caption"
          type="text"
          maxlength="200"
          :placeholder="defaults[i].caption"
          class="block w-full rounded-md border-gray-300 text-sm focus:border-pink-500 focus:ring-pink-500"
        >
        <p class="mt-1 text-xs text-gray-500">
          <span v-if="content.steps[i].caption">{{ (content.steps[i].caption || '').length }} / 200</span>
          <span v-else class="italic">Default: &ldquo;{{ defaults[i].caption }}&rdquo;</span>
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import TemplateImageInput from '../TemplateImageInput.vue';

/**
 * Editor for the V2 supporting-campaign modal's right-column hero. Three
 * slots (one per step in the supporting-campaign wizard): Email, Details,
 * Outcome. Each slot has an image and a short caption. Empty slots fall
 * through to V2SupportModalHero's hard-coded defaults at render time.
 *
 * Defaults below mirror STEP_IMAGES / STEP_CAPTIONS in
 * resources/js/components/campaigns/campaign-page/v2/V2SupportModalHero.vue.
 * Keep these two lists in sync — the editor surfaces them as placeholders
 * + "Default: ..." help text so admins see what would render if blank.
 */
const STEP_DEFAULTS = [
  {
    image: '/img/dig-pink-v2/fundraise-create.jpg',
    caption: "Let's get your email so we can set up your page.",
  },
  {
    image: '/img/dig-pink-v2/fundraise-share.jpg',
    caption: 'Tell us about your campaign and event.',
  },
  {
    image: '/img/dig-pink-v2/fundraise-grow.jpg',
    caption: "You're all set.",
  },
];

const STEPS = [
  { key: 'email',   label: 'Email',   role: 'opens the wizard — sets up the account' },
  { key: 'details', label: 'Details', role: 'collects the campaign + event info' },
  { key: 'outcome', label: 'Outcome', role: 'celebrates the published campaign' },
];

export default {
  name: 'SupportModalSectionEditor',
  components: { TemplateImageInput },
  props: {
    content: { type: Object, required: true },
    uploadUrl: { type: String, required: true },
  },
  emits: ['preview-step-change'],
  data() {
    return {
      steps: STEPS,
      defaults: STEP_DEFAULTS,
      activeStep: 1,
      cardRefs: [],
      // Mirrors V2SupportModalHero.DEFAULT_FOCAL — keeps the marker
      // visible at the production default crop until the admin
      // explicitly sets one.
      defaultFocal: { x: 50, y: 28 },
    };
  },
  created() {
    // The model pads to 3 entries on read, but a fresh discard/reset can
    // hand us a malformed array — guard so v-model never indexes undefined.
    if (!Array.isArray(this.content.steps)) {
      this.content.steps = [];
    }
    while (this.content.steps.length < 3) {
      this.content.steps.push({ image: null, caption: null, focal_point: { x: null, y: null } });
    }
    // Older saves predate focal_point — back-fill so v-model:focal-point
    // doesn't bind to undefined.
    this.content.steps.forEach(s => {
      if (!s.focal_point || typeof s.focal_point !== 'object') {
        s.focal_point = { x: null, y: null };
      }
    });
  },
  mounted() {
    // Push the initial active step up to the editor → preview pane on
    // first render so the modal opens to step 1's hero immediately.
    this.$emit('preview-step-change', this.activeStep);
  },
  beforeUpdate() {
    // Refs collected via :ref="el => cardRefs[i] = el" persist between
    // renders; reset so stale entries from a discard/replay don't leak.
    this.cardRefs = [];
  },
  methods: {
    setActiveStep(step, opts = {}) {
      if (this.activeStep === step) return;
      this.activeStep = step;
      this.$emit('preview-step-change', step);
      if (!opts.skipScroll) {
        // Smooth-scroll the matching card into view so editing focus
        // tracks what's previewing.
        const el = this.cardRefs[step - 1];
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    },
  },
};
</script>
